import { useEffect, useRef, useState } from "react";

import { Icon, Spinner } from "@blueprintjs/core";
import { Link, useNavigate, useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { ScenarioHeader } from "@/components/pages_logged_in/roleplays/details/ScenarioHeader";
import {
  buildCreateScenarioUrl,
  buildScenarioUrl,
} from "@/components/pages_logged_in/roleplays/utils";
import { NotFoundInAppPage } from "@/components/pages_public/404/NotFound";
import { Container } from "@/components/pieces/bootstrap";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { RED, SIDE_NAV_WIDTH } from "@/css/constants";
import { ScenarioIntakeQuery, useScenarioIntakeQuery } from "@/graphql";

const MESSAGE_INTERVAL = 6000;
const POLLING_INTERVAL = 2000;
const MAX_POLLING_ATTEMPTS = 120;

const waitingMessages = [
  "Reading the transcript...",
  "Writing objective...",
  "Writing scenario...",
  "Analyzing personalities...",
  "Training AI personality...",
  "Choosing character...",
  "Final touches...",
  "Almost there...",
  "Polishing results...",
  "Finalizing scenario...",
  "Almost done...",
  "Preparing insights...",
  "So close...",
  "Almost finished...",
  "It's coming...",
  "Just a moment...",
  "Almost ready...",
];

export type ScenarioIntake = NonNullable<
  NonNullable<ScenarioIntakeQuery["scenarioIntake"]>
>;

function isScenarioReady(scenarioIntake: ScenarioIntake) {
  return (
    scenarioIntake.processingStatus === "Processed" &&
    scenarioIntake?.scenario?.assistantId
  );
}

interface GeneratingScenarioProps {
  didTimeout: boolean;
  failed: boolean;
}

export function GeneratingScenario({
  didTimeout,
  failed,
}: GeneratingScenarioProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % waitingMessages.length);
    }, MESSAGE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  if (didTimeout) {
    return (
      <CardAboveXs>
        <Flex
          className="bas round-corners"
          backgroundColor="#FFF"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Icon icon="error" size={50} color={RED} />
          <Box mt={3} fontSize={18} fontWeight={600} textAlign="center">
            Hmm... This is taking longer than expected. Try refreshing the page.
          </Box>
          <Box mt={3}>
            <FlexibleButton
              variant="primary"
              onClick={() => window.location.reload()}
              icon={<Icon icon="refresh" />}
            >
              Refresh
            </FlexibleButton>
          </Box>
        </Flex>
      </CardAboveXs>
    );
  }

  if (failed) {
    return (
      <CardAboveXs>
        <Flex
          className="bas round-corners"
          backgroundColor="#FFF"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Icon icon="error" size={50} color={RED} />
          <Box mt={3} fontSize={18} fontWeight={600} textAlign="center">
            Unfortunately, we weren't able to generate a scenario.
          </Box>
          <Box mt={1}>
            This generally happens if the intake conversation didn't provide
            enough details. Please give it another try.
          </Box>
          <Box mt={3}>
            <FlexibleButton
              as={Link}
              variant="primary"
              to={buildCreateScenarioUrl()}
            >
              Start Again
            </FlexibleButton>
          </Box>
        </Flex>
      </CardAboveXs>
    );
  }

  return (
    <CardAboveXs>
      <Flex
        className="bas round-corners"
        backgroundColor="#FFF"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mt={4}
        p={4}
      >
        <Spinner size={50} />
        <Box mt={3} fontSize={18} fontWeight={600} textAlign="center">
          {waitingMessages[messageIndex]}
        </Box>
      </Flex>
    </CardAboveXs>
  );
}

export function IntakeProcessingPageContainer() {
  const navigate = useNavigate();
  const { intakeUuid } = useParams();
  const pollAttemptsRef = useRef(0);
  const isPollingRef = useRef(false);
  const [didTimeout, setDidTimeout] = useState(false);
  const [failed, setFailed] = useState(false);

  const { data, loading, refetch } = useScenarioIntakeQuery({
    fetchPolicy: "network-only",
    variables: { uuid: intakeUuid ?? "" },
  });

  const scenarioIntake = data?.scenarioIntake;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollData = async () => {
      if (isPollingRef.current) return;

      if (
        scenarioIntake &&
        !isScenarioReady(scenarioIntake) &&
        pollAttemptsRef.current < MAX_POLLING_ATTEMPTS
      ) {
        isPollingRef.current = true;
        pollAttemptsRef.current += 1;

        try {
          await refetch();
        } catch {
          setDidTimeout(true);
        } finally {
          isPollingRef.current = false;
        }

        if (
          !isScenarioReady(scenarioIntake) &&
          pollAttemptsRef.current < MAX_POLLING_ATTEMPTS
        ) {
          timeoutId = setTimeout(pollData, POLLING_INTERVAL);
        } else if (pollAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          setDidTimeout(true);
        }
      }
    };

    if (
      scenarioIntake &&
      !isScenarioReady(scenarioIntake) &&
      !isPollingRef.current
    ) {
      timeoutId = setTimeout(pollData, POLLING_INTERVAL);
    }

    return () => {
      clearTimeout(timeoutId);
      isPollingRef.current = false;
    };
  }, [scenarioIntake, refetch]);

  useEffect(() => {
    if (scenarioIntake?.scenario?.slug && isScenarioReady(scenarioIntake)) {
      navigate(
        buildScenarioUrl({
          roleplaySlug: scenarioIntake?.roleplay?.slug ?? undefined,
          scenarioSlug: scenarioIntake.scenario.slug,
        }),
      );
    } else if (scenarioIntake && scenarioIntake.processingStatus === "Failed") {
      setFailed(true);
    }
  }, [scenarioIntake, navigate]);

  if (!loading && data && !scenarioIntake) {
    return <NotFoundInAppPage />;
  }

  return (
    <>
      <SEOHead title="Generating Scenario - Exec" />
      <Nav pageLoading={loading} />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        <ScenarioHeader
          page={"intakeProcessing"}
          roleplay={scenarioIntake?.roleplay}
          loading={loading}
        />
        <Container>
          <Box mt={[24, 36]}>
            <GeneratingScenario didTimeout={didTimeout} failed={failed} />
          </Box>
        </Container>
      </Box>
    </>
  );
}

export function IntakeProcessingPage() {
  return (
    <Authenticate maxWidth="100%" layout="full">
      <IntakeProcessingPageContainer />
    </Authenticate>
  );
}
